import { initGoogleClient } from "./config";
import { authManagerInstance, AuthProvider } from "../values";

export const fetchGoogleCalendar = async (account, setEvents, setError) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        if (!window.gapi || !window.gapi.client || !window.gapi.client.calendar) {
            await initGoogleClient();
            if (!window.gapi || !window.gapi.client || !window.gapi.client.calendar) {
                throw new Error("Google Calendar API client not initialized properly");
            }
        }
        await validateGoogleAccount(account, setError);
        try {
            const timeMin = new Date();
            timeMin.setDate(timeMin.getDate() - 30);
            const timeMax = new Date();
            timeMax.setDate(timeMax.getDate() + 60);
            const response = await window.gapi.client.calendar.events.list({
                'calendarId': 'primary',
                'timeMin': timeMin.toISOString(),
                'timeMax': timeMax.toISOString(),
                'showDeleted': false,
                'singleEvents': true,
                'maxResults': 100,
                'orderBy': 'startTime'
            });

            const events = response.result.items.map(event => {
                const start = event.start.dateTime || event.start.date;
                const end = event.end.dateTime || event.end.date;
                const isAllDay = !event.start.dateTime;

                return {
                    id: event.id,
                    title: event.summary || 'No Title',
                    startDateTime: new Date(start),
                    endDateTime: new Date(end),
                    startTime: isAllDay ? 'All day' : new Date(start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    endTime: isAllDay ? 'All day' : new Date(end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    isAllDay: isAllDay,
                    location: event.location || 'No location',
                    description: event.description || '',
                    htmlLink: event.htmlLink,
                    attendees: event.attendees || [],
                    account: account,
                    provider: AuthProvider.GOOGLE
                };
            });

            setEvents && setEvents(events);
            return events;
        } catch (listError) {
            if (listError.status === 401 ||
                (listError.result && listError.result.error &&
                    (listError.result.error.status === 'UNAUTHENTICATED' ||
                        listError.result.error.code === 401))) {

                await initGoogleClient();
                const timeMin = new Date();
                timeMin.setDate(timeMin.getDate() - 30);
                const timeMax = new Date();
                timeMax.setDate(timeMax.getDate() + 60);

                const response = await window.gapi.client.calendar.events.list({
                    'calendarId': 'primary',
                    'timeMin': timeMin.toISOString(),
                    'timeMax': timeMax.toISOString(),
                    'showDeleted': false,
                    'singleEvents': true,
                    'maxResults': 100,
                    'orderBy': 'startTime'
                });

                const events = response.result.items.map(event => {
                    const start = event.start.dateTime || event.start.date;
                    const end = event.end.dateTime || event.end.date;
                    const isAllDay = !event.start.dateTime;

                    return {
                        id: event.id,
                        title: event.summary || 'No Title',
                        startDateTime: new Date(start),
                        endDateTime: new Date(end),
                        startTime: isAllDay ? 'All day' : new Date(start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        endTime: isAllDay ? 'All day' : new Date(end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        isAllDay: isAllDay,
                        location: event.location || 'No location',
                        description: event.description || '',
                        htmlLink: event.htmlLink,
                        attendees: event.attendees || [],
                        account: account,
                        provider: AuthProvider.GOOGLE
                    };
                });
                setEvents && setEvents(events);
                return events;
            } else {
                throw listError;
            }
        }
    } catch (error) {
        setError && setError(`Failed to fetch Google Calendar events: ${error.message}`);
        return [];
    }
};

export const createGoogleCalendarEvent = async (account, eventData, setEvents, setError, currentEvents) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        if (!window.gapi || !window.gapi.client || !window.gapi.client.calendar) {
            await initGoogleClient();
            if (!window.gapi || !window.gapi.client || !window.gapi.client.calendar) {
                throw new Error("Google Calendar API client not initialized properly");
            }
        }

        await validateGoogleAccount(account, setError);

        try {
            const event = {
                'summary': eventData.summary,
                'location': eventData.location,
                'description': eventData.description,
                'start': {
                    'dateTime': eventData.start.toISOString(),
                    'timeZone': Intl.DateTimeFormat().resolvedOptions().timeZone
                },
                'end': {
                    'dateTime': eventData.end.toISOString(),
                    'timeZone': Intl.DateTimeFormat().resolvedOptions().timeZone
                },
                'attendees': eventData.attendees || [],
                'reminders': {
                    'useDefault': false,
                    'overrides': [
                        { 'method': 'email', 'minutes': 24 * 60 },
                        { 'method': 'popup', 'minutes': 10 }
                    ]
                }
            };

            const response = await window.gapi.client.calendar.events.insert({
                'calendarId': 'primary',
                'resource': event
            });

            const createdEvent = {
                id: response.result.id,
                title: response.result.summary,
                startDateTime: new Date(response.result.start.dateTime),
                endDateTime: new Date(response.result.end.dateTime),
                startTime: new Date(response.result.start.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                endTime: new Date(response.result.end.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                location: response.result.location,
                description: response.result.description,
                htmlLink: response.result.htmlLink,
                account: account,
                provider: AuthProvider.GOOGLE
            };

            setEvents && setEvents([...currentEvents, createdEvent]);
            return createdEvent;
        } catch (createError) {
            if (createError.status === 401 ||
                (createError.result && createError.result.error &&
                    (createError.result.error.status === 'UNAUTHENTICATED' ||
                        createError.result.error.code === 401))) {
                await initGoogleClient();
                const event = {
                    'summary': eventData.summary,
                    'location': eventData.location,
                    'description': eventData.description,
                    'start': {
                        'dateTime': eventData.start.toISOString(),
                        'timeZone': Intl.DateTimeFormat().resolvedOptions().timeZone
                    },
                    'end': {
                        'dateTime': eventData.end.toISOString(),
                        'timeZone': Intl.DateTimeFormat().resolvedOptions().timeZone
                    },
                    'attendees': eventData.attendees || [],
                    'reminders': {
                        'useDefault': false,
                        'overrides': [
                            { 'method': 'email', 'minutes': 24 * 60 },
                            { 'method': 'popup', 'minutes': 10 }
                        ]
                    }
                };

                const response = await window.gapi.client.calendar.events.insert({
                    'calendarId': 'primary',
                    'resource': event
                });

                const createdEvent = {
                    id: response.result.id,
                    title: response.result.summary,
                    startDateTime: new Date(response.result.start.dateTime),
                    endDateTime: new Date(response.result.end.dateTime),
                    startTime: new Date(response.result.start.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    endTime: new Date(response.result.end.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    location: response.result.location,
                    description: response.result.description,
                    htmlLink: response.result.htmlLink,
                    account: account,
                    provider: AuthProvider.GOOGLE
                };
                setEvents && setEvents([...currentEvents, createdEvent]);
                return createdEvent;
            } else {
                throw createError;
            }
        }
    } catch (error) {
        setError && setError(`Failed to create Google Calendar event: ${error.message}`);
        return null;
    }
};

async function validateGoogleAccount(account, setError) {
    if (window.gapi && window.gapi.auth2) {
        const authInstance = window.gapi.auth2.getAuthInstance();
        if (authInstance) {
            const currentUser = authInstance.currentUser.get();
            if (currentUser.isSignedIn()) {
                const profile = currentUser.getBasicProfile();
                const currentEmail = profile.getEmail();
                let accountEmail;
                if (typeof account === 'string') {
                    const accountObj = authManagerInstance.getAccounts(AuthProvider.GOOGLE)
                        .find(acc => acc.id === account);
                    accountEmail = accountObj?.userData?.email;
                } else if (account.userData) {
                    accountEmail = account.userData.email;
                } else if (account.email) {
                    accountEmail = account.email;
                }

                if (accountEmail && currentEmail !== accountEmail) {
                    const errorMsg = `Please sign in with ${accountEmail} to access calendar. Current account: ${currentEmail}`;
                    setError && setError(errorMsg);
                    throw new Error(errorMsg);
                }
            } else {
                throw new Error("No Google user is signed in");
            }
        }
    }
}